import React, { useMemo, useState } from "react";
import "./ComparisonTable.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { TablePagination } from "@mui/material";

const CRITICAL_STATUSES = ["НЕТ В КАДРАХ", "НЕТ В БАНКЕ"];
const WARNING_STATUSES = ["РАЗНЫЕ ФИО"];

const isCriticalStatus = (status) => CRITICAL_STATUSES.includes(status);
const isWarningStatus = (status) => WARNING_STATUSES.includes(status);

const getRowClass = (status) => {
    if (isCriticalStatus(status)) return "row-critical";
    if (isWarningStatus(status)) return "row-warning";
    return "row-ok";
};

const getPriority = (row) => {
    if (isCriticalStatus(row["Статус"])) return 0;
    if (isWarningStatus(row["Статус"])) return 1;
    return 2;
};

const renderCellItems = (items) => {
    if (!items || items.length === 0) return "";
    if (items.length === 1) return items[0];

    return (
        <ul className="cell-list">
            {items.map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </ul>
    );
};

const joinCellItems = (items) => {
    if (!items || items.length === 0) return "";
    return items.join(" | ");
};

function ComparisonHRTable({ data = [], totals = null }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const p1 = getPriority(a);
            const p2 = getPriority(b);

            if (p1 !== p2) return p1 - p2;

            const fioA = (a["ФИО_банк"] || [""])[0] || "";
            const fioB = (b["ФИО_банк"] || [""])[0] || "";

            return fioA.localeCompare(fioB, "ru");
        });
    }, [data]);

    const paginatedData = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    const criticalCount = useMemo(() => {
        return sortedData.filter((row) => isCriticalStatus(row["Статус"])).length;
    }, [sortedData]);

    const warningCount = useMemo(() => {
        return sortedData.filter((row) => isWarningStatus(row["Статус"])).length;
    }, [sortedData]);

    const okCount = useMemo(() => {
        return sortedData.length - criticalCount - warningCount;
    }, [sortedData.length, criticalCount, warningCount]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Сверка HR");

        worksheet.columns = [
            { key: "iin_bank", width: 16 },
            { key: "fio_bank", width: 34 },
            { key: "iin_hr", width: 16 },
            { key: "fio_hr", width: 34 },
            { key: "status", width: 18 },
        ];

        worksheet.getCell("A1").value = "ИТОГО:";
        worksheet.getCell("A2").value = "Всего ИИН:";
        worksheet.getCell("B2").value = totals?.total_iins ?? 0;

        ["A1", "A2"].forEach((cellRef) => {
            worksheet.getCell(cellRef).font = { bold: true };
            worksheet.getCell(cellRef).alignment = { horizontal: "left" };
        });

        worksheet.getCell("B2").alignment = { horizontal: "left" };

        const headerRowNumber = 6;
        const headerRow = worksheet.getRow(headerRowNumber);

        headerRow.values = [
            "ИИН (банк)",
            "ФИО (банк)",
            "ИИН (HR)",
            "ФИО (HR)",
            "Статус",
        ];

        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "1F4E78" },
            };
            cell.alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true,
            };
        });

        headerRow.height = 28;

        sortedData.forEach((row) => {
            const excelRow = worksheet.addRow([
                row["ИИН_банк"] || "",
                joinCellItems(row["ФИО_банк"] || []),
                row["ИИН_hr"] || "",
                joinCellItems(row["ФИО_hr"] || []),
                row["Статус"] || "",
            ]);

            excelRow.eachCell((cell) => {
                cell.alignment = {
                    vertical: "top",
                    wrapText: true,
                };
            });

            const status = row["Статус"];
            const statusCell = excelRow.getCell(5);

            if (isCriticalStatus(status)) {
                statusCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FDECEC" },
                };
            }

            if (isWarningStatus(status)) {
                statusCell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFF8E1" },
                };
            }
        });

        worksheet.eachRow((row, rowNumber) => {
            if (rowNumber >= headerRowNumber) {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: "thin", color: { argb: "D9D9D9" } },
                        left: { style: "thin", color: { argb: "D9D9D9" } },
                        bottom: { style: "thin", color: { argb: "D9D9D9" } },
                        right: { style: "thin", color: { argb: "D9D9D9" } },
                    };
                });
            }
        });

        worksheet.autoFilter = {
            from: { row: headerRowNumber, column: 1 },
            to: { row: headerRowNumber, column: 5 },
        };

        worksheet.views = [
            {
                state: "frozen",
                ySplit: headerRowNumber,
            },
        ];

        const buffer = await workbook.xlsx.writeBuffer();

        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const now = new Date();
        const stamp =
            `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
                now.getDate()
            ).padStart(2, "0")}` +
            `_${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(
                2,
                "0"
            )}`;

        saveAs(blob, `comparison_hr_${stamp}.xlsx`);
    };

    if (!data.length) return null;

    return (
        <section className="comparison-section">
            <div className="comparison-header-card">
                <div className="comparison-header-main">
                    <div>
                        <div className="comparison-kicker">Результаты</div>
                        <h3 className="comparison-title">Сравнение банка и HR</h3>
                    </div>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="comparison-export-btn"
                    >
                        Скачать Excel
                    </button>
                </div>

                {totals && (
                    <div className="comparison-summary-grid comparison-summary-grid-hr">
                        <div className="summary-card">
                            <span className="summary-label">Всего ИИН</span>
                            <strong>{totals.total_iins ?? 0}</strong>
                        </div>
                    </div>
                )}

                <div className="comparison-legend">
                    <span className="legend-chip legend-critical">
                        Критические: {criticalCount}
                    </span>
                    <span className="legend-chip legend-warning">
                        Предупреждения: {warningCount}
                    </span>
                    <span className="legend-chip legend-ok">ОК: {okCount}</span>
                </div>
            </div>

            <div className="comparison-table-card">
                <div className="comparison-table-scroll">
                    <table className="comparison-table comparison-table-hr">
                        <thead>
                            <tr>
                                <th>ИИН (банк)</th>
                                <th>ФИО (банк)</th>
                                <th>ИИН (HR)</th>
                                <th>ФИО (HR)</th>
                                <th>Статус</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedData.map((row, index) => (
                                <tr key={index} className={getRowClass(row["Статус"])}>
                                    <td>{row["ИИН_банк"] || ""}</td>
                                    <td>{renderCellItems(row["ФИО_банк"] || [])}</td>
                                    <td>{row["ИИН_hr"] || ""}</td>
                                    <td>{renderCellItems(row["ФИО_hr"] || [])}</td>
                                    <td>{row["Статус"] || ""}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="comparison-pagination-wrap">
                    <TablePagination
                        component="div"
                        count={sortedData.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Строк на странице:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
                    />
                </div>
            </div>
        </section>
    );
}

export default ComparisonHRTable;